import "primeicons/primeicons.css";
import "./RTUList.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";

import { AppDispatch, RootState } from "../../redux/store/store";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Avatar } from "primereact/avatar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { applyRowSelectionAriaLabels } from "../RtuCluster/rowSelectionAriaLabels";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Paginator } from "primereact/paginator";
import { RTU } from "../../types/RTU";
import RTUActions from "../RTUActions"; // Import the new component
import { SkeletonRegion, SkeletonTable } from "../common/Skeleton/Skeleton";
import { Tag } from "primereact/tag";
import { defaultThemeColor } from '../../config/themeConfig';
import { fetchRTUs } from "../../redux/slices/rtuSlice";
import { getEmptyMessage } from "../../utils/tableConfig";
import { buildAssetStatusFilterOptions, getStatusColor, getStatusLabelKey } from "../../utils/statusConfig";
import { humanizeStatusReason, humanizeStatusSource } from "../../utils/statusDisplayConfig";
import { PaginatorRowsLabel, ROWS_PER_PAGE_OPTIONS, STANDARD_PAGE_REPORT_TEMPLATE, STANDARD_PAGINATOR_TEMPLATE } from "../../utils/paginatorConfig";
import { formatDisplayDateTime } from "../../utils/dateTimeFormat";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TruncatedValue from "../common/TruncatedValue/TruncatedValue";
import BulkSelectionCount from "../RtuCluster/BulkSelectionCount";
import { ClusterNameCell, WellIdCell, WellNameCell } from "../RtuCluster/WellCells";
import {
  buildRowSearchText,
  displayedRows,
  getWellView,
  hasAnyFormerWells,
  hasAnyWells,
  wellColumnText,
} from "../RtuCluster/rtuCluster";
import {
  buildClusterView,
  ColumnFilters,
  hasActiveColumnFilter,
  makeRowComparator,
  matchesColumnFilters,
} from "../RtuCluster/clusterView";
import { resolveRtuSerialNo } from "../../utils/rtuIdentity";
import { persistToggledColumns, useIsMobileViewport, withMobileColumnDefaults } from "../RtuCluster/mobileColumns";
import {
  ACTIONS_COLUMN_WIDTH,
  cellContent,
  columnMinPx,
  middleEllipsis,
  NO_RESIZER_PT,
  resolveColumnWidths,
  SELECTION_COLUMN_WIDTH,
  useColumnResize,
  useContentWidths,
  useWrapperWidth,
  WidthAccumulator,
  widthStyle,
} from "../RtuCluster/columnLayout";

const WELL_FIELDS = ["wellName", "wellId"];

/** Column filters match the raw value; the well columns hold every searched well (see wellColumnText). */
const filterValue = (row: any, field: string): unknown => row[field];

/** Value a column sorts on. Well columns use the well shown in the row (declared primary or record well). */
const sortValue = (row: any, field: string): unknown => {
  if (field === "wellName") {
    return getWellView(row).shown?.name ?? "";
  }
  if (field === "wellId") {
    return getWellView(row).shown?.wellId ?? "";
  }
  return row[field];
};

/** Phone column widths (the desktop widths come from RtuCluster/columnLayout). */
const desktopColumnWidths: { [key: string]: string } = {
  status: '70px',
  id: '70px',
  name: '150px',
  wellName: '170px',
  wellId: '100px',
  serialNo: '130px',
  location: '180px',
  configVersion: '120px',
  deploymentVersion: '150px',
  diskUsage: '100px',
  cpuUsage: '100px',
  statusObservedAtUtc: '150px',
  lastFileReceivedAtUtc: '170px',
  lastData: '120px',
  installDate: '120px',
  lastConfigUpdate: '150px',
  publicIPAddress: '130px',
  reportingToServer: '140px',
  groupId: '80px',
  provider: '120px',
  modemIP: '120px',
  ethernetIP: '120px',
  field: '100px',
};

/** Phones: the four priority columns plus the narrow actions button fit a 390px viewport without sideways scrolling. */
const MOBILE_COLUMN_WIDTHS: { [key: string]: string } = {
  status: '36px',
  name: '104px',
  wellName: '104px',
  wellId: '68px',
};
const MOBILE_ACTIONS_WIDTH_PX = 40;

/** Fields whose text can be longer than their column: they render through TruncatedValue (full value, copy control). */
const TRUNCATED_TEXT_FIELDS = [
  "location",
  "configVersion",
  "deploymentVersion",
  "publicIPAddress",
  "modemIP",
  "ethernetIP",
  "groupId",
  "provider",
  "field",
];
const TRUNCATED_TIMESTAMP_FIELDS = [
  "statusObservedAtUtc",
  "lastFileReceivedAtUtc",
  "lastData",
  "installDate",
  "lastConfigUpdate",
  "reportingToServer",
];

const COLUMN_VISIBILITY_STORAGE_PREFIX = "dms_column_visibility";
const ASSET_LIST_VIEW_KEY = "asset-list";

const resolveColumnVisibilityUserKey = (): string => {
  if (typeof window === "undefined") {
    return "anonymous";
  }

  try {
    const rawUser = window.localStorage.getItem("dms_auth_user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as Record<string, unknown>;
      const stableIdentity =
        parsed.username ??
        parsed.logins ??
        parsed.email ??
        parsed.id ??
        parsed.employeeId;
      if (stableIdentity !== undefined && stableIdentity !== null) {
        return String(stableIdentity);
      }
    }
  } catch (error) {
    console.warn("Failed to parse dms_auth_user for column persistence key", error);
  }

  return "anonymous";
};

const getColumnVisibilityStorageKey = (viewKey: string): string =>
  `${COLUMN_VISIBILITY_STORAGE_PREFIX}:${viewKey}:${resolveColumnVisibilityUserKey()}`;

const hydrateColumnVisibility = <T extends { field: string; visible: boolean }>(
  columns: T[],
  storageKey: string
): T[] => {
  if (typeof window === "undefined") {
    return columns;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return columns;
    }
    const parsed = JSON.parse(stored) as Record<string, boolean>;
    const hydrated = columns.map((column) =>
      Object.prototype.hasOwnProperty.call(parsed, column.field)
        ? { ...column, visible: Boolean(parsed[column.field]) }
        : column
    );
    if (hydrated.every((column) => !column.visible)) {
      console.warn("Stored column visibility hid every column; restoring defaults");
      return columns;
    }
    return hydrated;
  } catch (error) {
    console.warn("Failed to hydrate column visibility settings", error);
    return columns;
  }
};

const RTUList: React.FC = () => {
  const { t } = useTranslation();
  // before the table renders: the row checkboxes are named for the action (PrimeReact's defaults read inverted)
  applyRowSelectionAriaLabels(
    t('assetList.selectRow', { defaultValue: 'Select row' }),
    t('assetList.deselectRow', { defaultValue: 'Deselect row' }),
  );
  const { rtus, loading } = useSelector((state: RootState) => state.rtu || {});
  const dispatch = useDispatch<AppDispatch>();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    location: { value: null, matchMode: FilterMatchMode.CONTAINS },
    diskUsage: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    cpuUsage: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastData: { value: null, matchMode: FilterMatchMode.EQUALS },
    statusObservedAtUtc: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastFileReceivedAtUtc: { value: null, matchMode: FilterMatchMode.EQUALS },
    configVersion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    deploymentVersion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    installDate: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastConfigUpdate: { value: null, matchMode: FilterMatchMode.EQUALS },
    publicIPAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    localIPAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportingToServer: { value: null, matchMode: FilterMatchMode.EQUALS },
    groupId: { value: null, matchMode: FilterMatchMode.EQUALS },
    provider: { value: null, matchMode: FilterMatchMode.CONTAINS },
    modemIP: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ethernetIP: { value: null, matchMode: FilterMatchMode.CONTAINS },
    field: { value: null, matchMode: FilterMatchMode.CONTAINS },
    serialNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
    wellName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    wellId: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [collapsedMasters, setCollapsedMasters] = useState<Set<number>>(() => new Set());
  const [multiSortMeta, setMultiSortMeta] = useState<any[]>([]);
  // Paging counts root rows (standalone rows and masters); a master's visible children always stay on its page.
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [includeFormer, setIncludeFormer] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const columnStorageKeyRef = useRef<string>(getColumnVisibilityStorageKey(ASSET_LIST_VIEW_KEY));
  const touchedColumnsRef = useRef<Set<string>>(new Set());
  const isMobile = useIsMobileViewport();
  const toggleFilters = () => {
    setShowFilters((previous) => {
      const next = !previous;
      setColumns((currentColumns) =>
        currentColumns.map((column) => ({
          ...column,
          filter: next,
        }))
      );
      return next;
    });
  };
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<RTU[]>([]);
  // Phones hide the checkbox column and bulk actions until the compact Select mode is switched on.
  const [selectMode, setSelectMode] = useState(false);
  const showSelection = !isMobile || selectMode;
  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedRows([]);
    }
    setSelectMode(!selectMode);
  };
  const statusOptions = useMemo(() => buildAssetStatusFilterOptions(t), [t]);

  // A device is shown once: FORMER records whose asset has a CURRENT row are hidden.
  const shownRtus = useMemo(() => displayedRows(rtus), [rtus]);

  const tableRows = useMemo(
    () =>
      shownRtus.map((rtu) => {
        const serialNo = resolveRtuSerialNo(rtu) ?? "";
        return {
          ...rtu,
          serialNo,
          ...wellColumnText(rtu, { includeFormer }),
          searchText: buildRowSearchText({ ...rtu, serialNo }, { includeFormer }),
        };
      }),
    [includeFormer, shownRtus]
  );

  type TableRow = (typeof tableRows)[number];

  const hasWells = useMemo(() => hasAnyWells(shownRtus), [shownRtus]);
  const hasFormerWells = useMemo(() => hasAnyFormerWells(shownRtus), [shownRtus]);
  const hasRecordIdentity = useMemo(() => shownRtus.some((rtu) => rtu.recordState !== null && rtu.recordState !== undefined), [shownRtus]);
  const hasClusterData = useMemo(
    () => hasWells || shownRtus.some((rtu) => rtu.clusterParentId !== null && rtu.clusterParentId !== undefined),
    [hasWells, shownRtus]
  );
  // Cluster placement is kept for every sort, filter, collapse and page (see buildClusterView).
  const clusterMode = hasClusterData;
  const globalQuery = String((filters.global as any)?.value ?? "").trim().toLowerCase();
  const filterActive = globalQuery !== "" || hasActiveColumnFilter(filters as ColumnFilters);
  const matches = useMemo(
    () =>
      filterActive
        ? (row: TableRow) =>
            (globalQuery === "" || row.searchText.includes(globalQuery)) &&
            matchesColumnFilters(row, filters as ColumnFilters, filterValue)
        : null,
    [filterActive, filters, globalQuery]
  );
  const compare = useMemo(() => makeRowComparator<TableRow>(multiSortMeta, sortValue), [multiSortMeta]);
  const view = useMemo(
    () => buildClusterView(tableRows, { matches, compare, collapsed: collapsedMasters, first, pageSize }),
    [collapsedMasters, compare, first, matches, pageSize, tableRows]
  );
  const clusterInfo = view.info;
  const tableValue = view.rows;
  const allSelected = view.allRows.length > 0 && view.allRows.every((row) => selectedRows.some((selected) => selected.id === row.id));
  // Select-all acts on the rows the current search leaves visible; rows selected earlier that are now hidden stay selected.
  const onSelectAllChange = useCallback(
    (event: any) => {
      const visibleIds = new Set(view.allRows.map((row) => row.id));
      setSelectedRows((previous) => {
        const kept = previous.filter((row) => !visibleIds.has(row.id));
        return event.checked ? [...kept, ...(view.allRows as unknown as RTU[])] : kept;
      });
    },
    [view.allRows]
  );

  const activeWellQuery = (
    globalFilterValue.trim() ||
    String((filters.wellName as any)?.value ?? "") ||
    String((filters.wellId as any)?.value ?? "")
  ).trim();
  const rtuById = useMemo(() => {
    const map = new Map<number, RTU>();
    shownRtus.forEach((rtu) => map.set(rtu.id, rtu));
    return map;
  }, [shownRtus]);
  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedMasters((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const formatDateTime = (value: string | null | undefined) => formatDisplayDateTime(value);

  const statusTooltip = (rowData: RTU) => {
    const source = humanizeStatusSource(rowData.statusSource);
    const reason = rowData.statusReason ? ` - ${humanizeStatusReason(rowData.statusReason)}` : "";
    const statusLabel = t(getStatusLabelKey(rowData.status), { defaultValue: String(rowData.status ?? "") });

    return `${statusLabel} (${source})${reason}`;
  };

  const statusBodyTemplate = (rowData: RTU) => {
    const displayStatus = rowData.systemRestart === true ? "rebooting" : rowData.status;
    return (
      <span title={statusTooltip(rowData)} aria-label={statusTooltip(rowData)}>
        <Avatar
          style={{
            backgroundColor: getSeverity(displayStatus),
            width: "20px",
            height: "20px",
          }}
          shape="circle"
        />
      </span>
    );
  };

  const statusItemTemplate = (option: any) => {
    return (
      <Tag className="asset-status-filter-tag" value={option.label} style={{ backgroundColor: getSeverity(option.value) }} />
    );
  };

  const statusValueTemplate = (option: any) => {
    if (!option) {
      return <span />;
    }

    return (
      <Tag className="asset-status-filter-tag asset-status-filter-tag--value" value={option.label} style={{ backgroundColor: getSeverity(option.value) }} />
    );
  };

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        itemTemplate={statusItemTemplate}
        valueTemplate={statusValueTemplate}
        optionLabel="label"
        optionValue="value"
        placeholder=""
        ariaLabel={t('assetList.filterColumn', { column: t('assetList.columns.status'), defaultValue: 'Filter {{column}}' })}
        aria-label={t('assetList.filterColumn', { column: t('assetList.columns.status'), defaultValue: 'Filter {{column}}' })}
        className="p-column-filter asset-status-filter-dropdown"
        panelClassName="asset-status-filter-panel"
        showClear
        style={{ width: "100%", minWidth: "4rem" }}
      />
    );
  };

  // The row filter's default text input has no accessible name (no label, no placeholder). Same input and behaviour
  // (applies on every change), named "Filter <column header>"; nothing visible is added.
  const columnTextFilter = (col: { header?: unknown; field: string }) => (options: any) => (
    <InputText
      className="p-column-filter"
      value={options.value ?? ""}
      onChange={(e) => options.filterApplyCallback(e.target.value)}
      aria-label={t('assetList.filterColumn', {
        column: typeof col.header === 'string' && col.header.trim() ? col.header : col.field,
        defaultValue: 'Filter {{column}}',
      })}
    />
  );

  const diskUsageBodyTemplate = (rowData: any) => {
    return `${rowData.diskUsage.toFixed(1)}%`;
  };

  const cpuUsageBodyTemplate = (rowData: any) => {
    return `${rowData.cpuUsage.toFixed(1)}%`;
  };

  const dateTemplate = (rowData: any) => formatDateTime(rowData.lastData);
  const installDateTemplate = (rowData: any) => formatDateTime(rowData.installDate);
  const statusObservedTemplate = (rowData: RTU) => formatDateTime(rowData.statusObservedAtUtc);
  const lastFileReceivedTemplate = (rowData: RTU) =>
    formatDateTime(rowData.lastFileReceivedAtUtc);
  const reportingToServerTemplate = (rowData: RTU) => formatDateTime(rowData.reportingToServer);
  const lastConfigUpdateTemplate = (rowData: RTU) => formatDateTime(rowData.lastConfigUpdate);

  const tableRef = useRef<any>(null);
  const resizeFieldsRef = useRef<Array<string | undefined>>([]);
  // PrimeReact's DataTable pins every column with an injected stylesheet after a drag; the widths live in React state
  // instead (so hiding / showing a column keeps working), and the injected sheet is dropped once they are committed.
  const dropInjectedWidths = useCallback(() => tableRef.current?.resetResizeColumnsWidth?.(), []);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentWidthsRef = useRef<Record<string, number>>({});
  const { widths: columnWidthOverrides, onColumnResizeEnd, onMouseDownCapture, shouldIgnoreSort, forgetWidth } =
    useColumnResize(() => resizeFieldsRef.current, (field) => columnMinPx(field, contentWidthsRef.current), dropInjectedWidths);

  const [columns, setColumns] = useState(() => withMobileColumnDefaults([
    {
      field: "status",
      header: t("assetList.columns.status"),
      headerClassName: "custom-header-status",
      visible: true,
      filterElement: statusRowFilterTemplate,
      body: statusBodyTemplate,
      filter: true,
    },
    // ID column hidden for the cluster layout: the well ID is already shown in "Primary well" as "name [ID]".
    // {
    //   field: "id",
    //   header: t("assetList.columns.id"),
    //   headerClassName: "custom-header-id",
    //   visible: true,
    //   filterElement: null,
    //   body: null,
    //   filter: true,
    // },
    {
      field: "name",
      header: t("assetList.columns.rtuName", { defaultValue: "RTU name" }),
      headerClassName: "custom-header-name",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "serialNo",
      header: t("assetList.columns.serialNo", { defaultValue: "Serial no." }),
      headerClassName: "custom-header-serial-no",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "wellName",
      header: t("assetList.columns.wellName", { defaultValue: "Wells" }),
      headerClassName: "custom-header-well-name",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "wellId",
      header: t("assetList.columns.wellId", { defaultValue: "Well ID" }),
      headerClassName: "custom-header-well-id",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "location",
      header: t("assetList.columns.location"),
      headerClassName: "custom-header-location",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "configVersion",
      header: t("assetList.columns.configVersion"),
      headerClassName: "custom-header-config-version",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "deploymentVersion",
      header: t("assetList.columns.deploymentVersion"),
      headerClassName: "custom-header-deployment-version",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "diskUsage",
      header: t("assetList.columns.diskUsage"),
      headerClassName: "custom-header-disk-usage",
      visible: true,
      filterElement: null,
      body: diskUsageBodyTemplate,
      filter: true,
    },
    {
      field: "cpuUsage",
      header: t("assetList.columns.cpuUsage"),
      headerClassName: "custom-header-cpu-usage",
      visible: true,
      filterElement: null,
      body: cpuUsageBodyTemplate,
      filter: true,
    },
    {
      field: "statusObservedAtUtc",
      header: t("assetList.columns.lastChecked", { defaultValue: "Last Checked" }),
      headerClassName: "custom-header-last-checked",
      visible: true,
      filterElement: null,
      body: statusObservedTemplate,
      filter: true,
    },
    {
      field: "lastFileReceivedAtUtc",
      header: t("assetList.columns.lastDataReceived", { defaultValue: "Last Data Received" }),
      headerClassName: "custom-header-last-data-received",
      visible: true,
      filterElement: null,
      body: lastFileReceivedTemplate,
      filter: true,
    },
    {
      field: "lastData",
      header: t("assetList.columns.lastData"),
      headerClassName: "custom-header-last-data",
      visible: false,
      filterElement: null,
      body: dateTemplate,
      filter: true,
    },
    {
      field: "installDate",
      header: t("assetList.columns.installDate"),
      headerClassName: "custom-header-install-date",
      visible: false,
      filterElement: null,
      body: installDateTemplate,
      filter: true,
    },
    {
      field: "lastConfigUpdate",
      header: t("assetList.columns.lastConfigUpdate"),
      headerClassName: "custom-header-last-config-update",
      visible: false,
      filterElement: null,
      body: lastConfigUpdateTemplate,
      filter: true,
    },
    {
      field: "publicIPAddress",
      header: t("assetList.columns.publicIp"),
      headerClassName: "custom-header-public-ipaddress",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "reportingToServer",
      header: t("assetList.columns.reportingToServer"),
      headerClassName: "custom-header-reportingtoserver",
      visible: false,
      filterElement: null,
      body: reportingToServerTemplate,
      filter: true,
    },
    {
      field: "groupId",
      header: t("assetList.columns.groupId"),
      headerClassName: "custom-header-group-id",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "provider",
      header: t("assetList.columns.provider"),
      headerClassName: "custom-header-provider",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "modemIP",
      header: t("assetList.columns.modemIp"),
      headerClassName: "custom-header-modem-ip",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "ethernetIP",
      header: t("assetList.columns.ethernetIp"),
      headerClassName: "custom-header-ethernet-ip",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "field",
      header: t("assetList.columns.field"),
      headerClassName: "custom-header-field",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
  ]));

  const op: any = useRef(null);

  // Well columns exist once the API reports wells or record identity (older APIs carry neither).
  const showWellColumns = hasWells || hasRecordIdentity;
  const isColumnAvailable = (col: { field: string }) => showWellColumns || !WELL_FIELDS.includes(col.field);

  const onColumnsChange = useCallback((field: any, isChecked: any) => {
    touchedColumnsRef.current.add(String(field));
    forgetWidth(String(field));
    setColumns((currentColumns) => {
      const visibleCount = currentColumns.filter((col) => col.visible).length;
      const isLastVisibleColumn =
        !isChecked && visibleCount === 1 && currentColumns.find((col) => col.field === field)?.visible;
      if (isLastVisibleColumn) {
        return currentColumns;
      }
      return currentColumns.map((col) => {
        if (col.field === field) {
          return { ...col, visible: isChecked };
        }
        return col;
      });
    });
  }, [forgetWidth]);

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };
    //@ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const renderHeader = () => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: isMobile && showFilters ? "1 1 100%" : 1, minWidth: 0 }}>
          {showFilters && (
            <div
              className="asset-toolbar__search"
              style={isMobile ? { display: 'flex', flex: 1, minWidth: 0 } : { display: 'flex', justifyContent: 'center', maxWidth: '200px' }}
            >
              <span className="p-input-icon-left" style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                <i className="pi pi-search" style={{ fontSize: "0.7rem", left: '0.5rem' }} />
                <InputText
                  value={globalFilterValue}
                  onChange={onGlobalFilterChange}
                  placeholder=""
                  aria-label={t('assetList.searchRtus', { defaultValue: 'Search RTUs' })}
                  style={{ fontSize: "0.75rem", height: "1.5rem", paddingLeft: "1.5rem", width: "100%" }}
                />
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-end", marginLeft: "auto", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <i
              className="pi pi-search"
              style={{ cursor: "pointer", fontSize: "0.9rem", fontWeight: 700, color: showFilters ? defaultThemeColor : "#333", padding: "0.25rem" }}
              title={showFilters ? t('search.hide') : t('search.show')}
              onClick={toggleFilters}
            />
            <i
              className="pi pi-list"
              style={{ cursor: "pointer", fontSize: "0.9rem", fontWeight: 700, color: "#333", padding: "0.25rem" }}
              title={t('table.selectColumns')}
              onClick={(e) => op.current.toggle(e)}
            />
            <OverlayPanel ref={op} closeOnEscape dismissable={true} className="asset-column-picker">
              {columns.filter(isColumnAvailable).map((col) => (
                <div key={col.field} className="asset-column-picker__row">
                  <Checkbox
                    inputId={`cb-${col.field}`}
                    checked={col.visible}
                    onChange={(e) => onColumnsChange(col.field, e.checked)}
                    className="asset-column-picker__checkbox"
                  />
                  <label htmlFor={`cb-${col.field}`} className="asset-column-picker__label">{col.header}</label>
                </div>
              ))}
              {hasFormerWells && (
                <div className="asset-column-picker__section">
                  <div className="asset-column-picker__section-title">{t('assetList.view', { defaultValue: 'View' })}</div>
                  <div className="asset-column-picker__row">
                    <Checkbox
                      inputId="asset-include-former-wells"
                      checked={includeFormer}
                      onChange={(e) => {
                        setIncludeFormer(Boolean(e.checked));
                        setFirst(0);
                      }}
                      className="asset-column-picker__checkbox"
                    />
                    <label htmlFor="asset-include-former-wells" className="asset-column-picker__label">
                      {t('assetList.includeFormerWells', { defaultValue: 'Include former wells' })}
                    </label>
                  </div>
                </div>
              )}
            </OverlayPanel>
            {isMobile && (
            <button
              type="button"
              className="asset-select-toggle"
              aria-label={t('assetList.selectMode', { defaultValue: 'Select RTUs' })}
              title={t('assetList.selectMode', { defaultValue: 'Select RTUs' })}
              aria-pressed={selectMode}
              onClick={toggleSelectMode}
              style={{ color: selectMode ? defaultThemeColor : '#333' }}
            >
              <i className="pi pi-check-square" />
            </button>
            )}
          </div>
          {showSelection && (
            <div style={{ display: "flex", justifyContent: "flex-end", minWidth: "max-content" }}>
              <BulkSelectionCount count={selectedRows.length}><RTUActions mode="bulk" selectedRows={selectedRows} selectedCount={selectedRows.length} /></BulkSelectionCount>
            </div>
          )}
        </div>
      </div>
    );
  };

  const header = renderHeader();
  const visibleColumns = columns.filter((col) => col.visible && isColumnAvailable(col));
  // header cells in order, for a drag that would take a column below its min width
  resizeFieldsRef.current = [...(showSelection ? [undefined] : []), ...visibleColumns.map((col) => col.field), undefined];

  // Desktop widths: each column is as wide as its longest value (header, or any row of the whole data set), the table
  // scrolls sideways when they do not fit, and shares out spare room when they do (see resolveColumnWidths).
  const visibleKey = visibleColumns.map((col) => `${col.field}:${String(col.header)}`).join('|');
  const contentWidths = useContentWidths({
    enabled: !isMobile,
    rootRef,
    deps: [tableRows, visibleKey, clusterMode],
    compute: (measurer) => {
      const accumulator = new WidthAccumulator(measurer);
      visibleColumns.forEach((col) => accumulator.header(col.field, String(col.header)));
      tableRows.forEach((row) =>
        visibleColumns.forEach((col) => {
          const content = cellContent(row, col.field, clusterMode, measurer);
          if (content) {
            accumulator.value(col.field, content.text, content.extra, content.bold);
          }
        })
      );
      return accumulator.result();
    },
  });
  contentWidthsRef.current = contentWidths;
  const wrapperWidth = useWrapperWidth(rootRef, '.p-datatable-wrapper', !isMobile);
  const resolved = resolveColumnWidths({
    fields: visibleColumns.map((col) => col.field),
    content: contentWidths,
    manual: columnWidthOverrides,
    containerWidth: wrapperWidth,
    fixedPx: (showSelection ? SELECTION_COLUMN_WIDTH : 0) + ACTIONS_COLUMN_WIDTH,
  });
  // PrimeReact sets the table width itself while a drag ends; React's value is the one that counts
  useLayoutEffect(() => {
    const table = rootRef.current?.querySelector('.p-datatable-table') as HTMLElement | null;
    if (table && !isMobile) {
      table.style.width = `${resolved.total}px`;
      table.style.minWidth = `${resolved.total}px`;
    }
  });

  const rowButtonTemplate = (rowData: RTU) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }} onClick={(event) => event.stopPropagation()}>
        <RTUActions mode="row" selectedRTUIp={rowData.publicIPAddress} selectedRows={[rowData]} />
      </div>
    );
  };

  const onSelectionChanged = useCallback((e: any) => {
    e.originalEvent?.stopPropagation?.();
    setSelectedRows(e.value ?? []);
  }, []);

  // A row tap opens the RTU details. In phone Select mode a tap only selects (the DataTable toggles the checkbox), so
  // nothing navigates by accident; buttons, links and inputs inside a row never reach this handler.
  const onRowClick = (event: any) => {
    if (isMobile && selectMode) {
      return;
    }
    const target = event.originalEvent?.target as HTMLInputElement | undefined;
    if (target?.type === "checkbox") {
      return;
    }
    const rtuId = event.data.id;
    const rtu = rtus.find((rtu) => rtu.id === rtuId);
    navigate(`/rtupage/${rtuId}`, { state: { rtu } });
  };

  const navigate = useNavigate();
  const columnLabel = (field: string) => String(columns.find((col) => col.field === field)?.header ?? field);
  const openDetails = (rtuId: number) => navigate(`/rtupage/${rtuId}`, { state: { rtu: rtuById.get(rtuId) } });

  /** A FORMER record that is not merged into a CURRENT row: an ordinary row with a former-record marker, no tree context. */
  const isStandaloneFormer = (row: TableRow) => row.recordState === "FORMER";

  /** Cluster block styling: master row, child rows, and the row that closes the block. */
  const clusterRowClass = (row: TableRow) => {
    const info = clusterMode && !isStandaloneFormer(row) ? clusterInfo[row.id] : undefined;
    if (!info) {
      return {};
    }
    const isMaster = info.depth === 0 && info.childCount > 0;
    const isChild = info.depth === 1;
    return {
      "rtu-cluster-row--master": isMaster,
      "rtu-cluster-row--child": isChild,
      "rtu-cluster-row--last": (isChild && info.isLastChild) || (isMaster && !info.expanded),
    };
  };

  const nameBodyTemplate = (row: TableRow) => {
    const former = isStandaloneFormer(row);
    const info = clusterMode && !former ? clusterInfo[row.id] : undefined;
    return (
      <ClusterNameCell
        name={row.name}
        columnLabel={columnLabel("name")}
        depth={info?.depth ?? 0}
        slot={clusterMode}
        isLastChild={info?.isLastChild ?? false}
        childCount={info?.childCount ?? 0}
        expanded={info?.expanded ?? false}
        onToggle={() => toggleCollapsed(row.id)}
        onOpenDetails={() => openDetails(row.id)}
        marker={former ? "former" : row.recordState === "UNRESOLVED" ? "unresolved" : undefined}
      />
    );
  };

  const bodyOverrides: { [field: string]: (row: TableRow) => React.ReactNode } = {
    name: nameBodyTemplate,
    serialNo: (row) => (
      <TruncatedValue value={row.serialNo} shortDisplay={middleEllipsis(String(row.serialNo ?? ''))} label={columnLabel("serialNo")} />
    ),
    ...Object.fromEntries(
      TRUNCATED_TEXT_FIELDS.map((field) => [
        field,
        (row: TableRow) => <TruncatedValue value={(row as any)[field]} label={columnLabel(field)} />,
      ])
    ),
    ...Object.fromEntries(
      TRUNCATED_TIMESTAMP_FIELDS.map((field) => [
        field,
        (row: TableRow) => <TruncatedValue value={formatDateTime((row as any)[field])} label={columnLabel(field)} />,
      ])
    ),
    wellName: (row) => (
      <WellNameCell
        rtu={row}
        columnLabel={columnLabel("wellName")}
        query={activeWellQuery}
        resolveRtu={(id) => {
          const found = rtuById.get(id);
          return found ? { id: found.id, name: found.name } : undefined;
        }}
      />
    ),
    wellId: (row) => <WellIdCell rtu={row} columnLabel={columnLabel("wellId")} query={activeWellQuery} />,
  };
  const getSeverity = (status: string) => {
    return getStatusColor(status);
  };

  useEffect(() => {
    dispatch(fetchRTUs({}));
  }, [dispatch]);

  useEffect(() => {
    setColumns((currentColumns) =>
      hydrateColumnVisibility(currentColumns, columnStorageKeyRef.current)
    );
  }, []);

  // Only columns the user toggled are stored (merged), so defaults never masquerade as a saved choice.
  useEffect(() => {
    persistToggledColumns(columns, touchedColumnsRef.current, columnStorageKeyRef.current);
  }, [columns]);

  return (
    <div ref={rootRef} className="card" style={{ padding: '0.25rem', overflow: 'hidden' }} onMouseDownCapture={onMouseDownCapture}>
      <h1 className="dms-visually-hidden">{t('navigation.assetList')}</h1>
      <DataTable
        ref={tableRef}
        value={tableValue}
        rowClassName={clusterRowClass}
        className="small-datatable"
        size="small"
        selectionAriaLabel="name"
        {...(showFilters ? { filterDisplay: "row" } : {})}
        lazy
        filters={filters}
        onFilter={(e) => {
          setFilters(e.filters);
          setFirst(0);
        }}
        multiSortMeta={multiSortMeta}
        onSort={(e: any) => {
          // the click that ends a column drag is part of the drag; it must not sort
          if (shouldIgnoreSort()) {
            return;
          }
          setMultiSortMeta(e.multiSortMeta ?? []);
          setFirst(0);
        }}
        header={header}
        sortMode="multiple"
        removableSort
        selectionMode={null}
        selection={selectedRows}
        onSelectionChange={onSelectionChanged}
        selectAll={allSelected}
        onSelectAllChange={onSelectAllChange}
        scrollable
        resizableColumns={!isMobile}
        columnResizeMode="expand"
        onColumnResizeEnd={onColumnResizeEnd}
        dataKey="id"
        tableStyle={
          isMobile
            ? {
                tableLayout: 'fixed',
                width: '100%',
                minWidth: `${
                  visibleColumns.reduce(
                    (total, col) => total + parseInt(MOBILE_COLUMN_WIDTHS[col.field] ?? desktopColumnWidths[col.field] ?? '100px', 10),
                    0
                  ) + MOBILE_ACTIONS_WIDTH_PX + (showSelection ? 40 : 0)
                }px`,
              }
            : { tableLayout: 'fixed' as const, width: `${resolved.total}px`, minWidth: `${resolved.total}px` }
        }
        emptyMessage={loading ? (
          <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
            <SkeletonTable columns={[0.6, 0.6, 1, 1.4, 1, 1.2, 0.8, 0.8, 1.2, 1.3]} rows={10} showHeader={false} />
          </SkeletonRegion>
        ) : getEmptyMessage(t)}
        onRowClick={onRowClick}
        showGridlines
      >
        {showSelection && (
          <Column
            selectionMode="multiple"
            style={
              isMobile
                ? { width: "40px", minWidth: "40px", maxWidth: "40px" }
                : { width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px` }
            }
            headerStyle={
              isMobile
                ? { width: "40px", minWidth: "40px", maxWidth: "40px" }
                : { width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px` }
            }
            {...(isMobile ? {} : ({ pt: NO_RESIZER_PT } as any))}
            bodyClassName={isMobile ? "asset-select-column" : undefined}
            headerClassName={isMobile ? "asset-select-column" : undefined}
          ></Column>
        )}
        {visibleColumns.map((col) => {
            // Columns that should be centered (numeric, percentage, status, icons)
            const centerCols = ['status', 'id', 'diskUsage', 'cpuUsage', 'groupId'];
            const isCenter = centerCols.includes(col.field);
            
            const mobileWidths: { [key: string]: string } = { ...desktopColumnWidths, ...MOBILE_COLUMN_WIDTHS };

            return (
              <Column
                key={col.field}
                field={col.field}
                filter={showFilters}
                showFilterMenu={false}
                sortable
                headerClassName={col.headerClassName}
                header={col.header}
                style={isMobile ? { width: mobileWidths[col.field] || '100px' } : widthStyle(col.field, resolved.widths, contentWidths)}
                bodyStyle={{ textAlign: isCenter ? "center" : "left" }}
                filterElement={col.filterElement ?? columnTextFilter(col)}
                body={bodyOverrides[col.field] ?? col.body}
              />
            );
          })}
        <Column
          body={rowButtonTemplate}
          filter={showFilters}
                    frozen
          alignFrozen="right"
          filterElement={() => <span></span>}
          header={t('table.actions')}
          headerClassName="asset-list-actions-column"
          bodyClassName="asset-list-actions-column"
          style={{ width: isMobile ? `${MOBILE_ACTIONS_WIDTH_PX}px` : `${ACTIONS_COLUMN_WIDTH}px` }}
          headerStyle={{ textAlign: 'center' }}
          bodyStyle={{ textAlign: 'center', overflow: 'visible' }}
        ></Column>
      </DataTable>
      {/* The page holds N root rows plus their visible children, which the DataTable's own paginator cannot express. */}
      <Paginator
        first={view.first}
        rows={pageSize}
        totalRecords={view.rootCount}
        onPageChange={(e) => {
          setFirst(e.first);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        leftContent={<PaginatorRowsLabel />}
        template={STANDARD_PAGINATOR_TEMPLATE}
        currentPageReportTemplate={STANDARD_PAGE_REPORT_TEMPLATE}
        className="compact-paginator"
      />
    </div>
  );
};

export default RTUList;
